import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Card,
    Table,
    Button,
    Tag,
    Divider,
    Spin,
    Space,
    Row,
    Col,
    Descriptions,
} from 'antd';
import { ArrowLeftOutlined, ShoppingOutlined } from '@ant-design/icons';
import orderService from '../services/orderService';
import { formatPrice, formatDateTime, getOrderStatusColor, getOrderStatusText } from '../utils/formatters';
import ErrorMessage from '../components/common/ErrorMessage';

const { Title, Text } = Typography;

const OrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrder = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await orderService.getById(id);
                setOrder(response.order);
            } catch (err) {
                setError(err.response?.data?.message || 'Buyurtma ma\'lumotlarini yuklashda xatolik yuz berdi');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spin size="large" tip="Yuklanmoqda..." />
            </div>
        );
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    if (!order) {
        return <ErrorMessage message="Buyurtma topilmadi" />;
    }

    const columns = [
        {
            title: 'Mahsulot',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (text, record) => (
                <Space>
                    {record.product?.image_urls?.[0] && (
                        <img
                            src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${record.product.image_urls[0]}`}
                            alt={text}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        />
                    )}
                    <Text>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Narx',
            dataIndex: 'product_price',
            key: 'product_price',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Soni',
            dataIndex: 'quantity',
            key: 'quantity',
        },
        {
            title: 'Jami',
            dataIndex: 'subtotal',
            key: 'subtotal',
            render: (subtotal) => <Text strong>{formatPrice(subtotal)}</Text>,
        },
    ];

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 0' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/orders')}
                    >
                        Orqaga
                    </Button>
                    <Title level={3} style={{ margin: 0 }}>
                        Buyurtma #{order.order_number}
                    </Title>
                </Space>
                <Tag color={getOrderStatusColor(order.status)} style={{ fontSize: 16, padding: '4px 12px' }}>
                    {getOrderStatusText(order.status).toUpperCase()}
                </Tag>
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                    <Card title="Mahsulotlar" style={{ marginBottom: 24 }}>
                        <Table
                            columns={columns}
                            dataSource={order.orderItems}
                            rowKey="id"
                            pagination={false}
                            footer={() => (
                                <div style={{ textAlign: 'right' }}>
                                    <Text size="large">Jami summa: </Text>
                                    <Title level={4} style={{ display: 'inline', margin: 0, color: '#1890ff' }}>
                                        {formatPrice(order.total_amount)}
                                    </Title>
                                </div>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="Buyurtma ma'lumotlari" style={{ marginBottom: 24 }}>
                        <Descriptions column={1}>
                            <Descriptions.Item label="Sana">
                                {formatDateTime(order.createdAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Manzil">
                                {order.delivery_address}
                            </Descriptions.Item>
                            <Descriptions.Item label="Viloyat/Region">
                                {order.region?.name || 'Noma\'lum'}
                            </Descriptions.Item>
                        </Descriptions>

                        {order.customer_note && (
                            <>
                                <Divider style={{ margin: '12px 0' }} />
                                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Sizning izohingiz:</Text>
                                <Text italic>{order.customer_note}</Text>
                            </>
                        )}

                        {order.seller_note && (
                            <>
                                <Divider style={{ margin: '12px 0' }} />
                                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Sotuvchi izohi:</Text>
                                <Text strong color="blue">{order.seller_note}</Text>
                            </>
                        )}
                    </Card>

                    {order.seller && (
                        <Card title="Sotuvchi">
                            <Descriptions column={1}>
                                <Descriptions.Item label="Ism">
                                    {order.seller.first_name} {order.seller.last_name}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}

                    <Button
                        type="primary"
                        block
                        size="large"
                        icon={<ShoppingOutlined />}
                        onClick={() => navigate('/products')}
                        style={{ marginTop: 24 }}
                    >
                        Yana xarid qilish
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default OrderDetailPage;
